import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { TextFormatButtons } from './tokenTools';

const API_BASE = process.env.REACT_APP_URL || 'http://localhost:3501';

const APP_PAGE_OPTIONS = [
  { value: '/', label: 'Home Page (/)' },
  { value: '/announcements/', label: 'Announcements (/announcements/)' },
  { value: '/meetings/', label: 'Meetings (/meetings/)' },
  { value: '/profile/', label: 'Profile (/profile/)' },
  { value: '/users', label: 'Users (/users)' },
  { value: '/tm-tools/contest-randomizer', label: 'Contest Randomizer' },
  { value: '{{meeting.nextMeetingLink}}', label: 'Next Meeting Link (dynamic)' },
  { value: '{{club.website}}', label: 'Club Website (dynamic)' }
];

const FILE_TYPE_OPTIONS = [
  { value: 'jpg', label: 'JPG', mimeType: 'image/jpeg' },
  { value: 'png', label: 'PNG', mimeType: 'image/png' },
  { value: 'webp', label: 'WEBP', mimeType: 'image/webp' }
];

const MIME_TO_FILE_TYPE = FILE_TYPE_OPTIONS.reduce((acc, entry) => {
  acc[entry.mimeType] = entry.value;
  return acc;
}, {});

const inferMimeFromUrl = (value) => {
  const url = String(value || '').trim().toLowerCase();
  if (url.endsWith('.png')) return 'image/png';
  if (url.endsWith('.webp')) return 'image/webp';
  if (url.endsWith('.jpg') || url.endsWith('.jpeg')) return 'image/jpeg';
  return '';
};

const isDataUrl = (value) => /^data:/i.test(String(value || '').trim());

const resolveStoredImageUrl = (primary, fallback) => {
  const first = String(primary || '').trim();
  const second = String(fallback || '').trim();
  if (first && !isDataUrl(first)) return first;
  if (second && !isDataUrl(second)) return second;
  return '';
};

const clamp = (value, min, max, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
};

const decodeBase64Bytes = (dataUrl) => {
  if (typeof dataUrl !== 'string') return 0;
  const payload = dataUrl.split(',')[1] || '';
  if (!payload) return 0;
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding);
};

const getTargetPixels = (draftState, sourceWidthPx, sourceHeightPx) => {
  if (draftState.resizeUnit === '%') {
    return {
      widthPx: clamp(Math.round((sourceWidthPx * draftState.width) / 100), 1, 5000, sourceWidthPx),
      heightPx: clamp(Math.round((sourceHeightPx * draftState.height) / 100), 1, 5000, sourceHeightPx)
    };
  }

  return {
    widthPx: clamp(Math.round(draftState.width), 1, 5000, sourceWidthPx),
    heightPx: clamp(Math.round(draftState.height), 1, 5000, sourceHeightPx)
  };
};

const renderResizedPreview = async (sourceUrl, draftState) => {
  if (!sourceUrl) {
    return null;
  }

  const image = await new Promise((resolve, reject) => {
    const element = new Image();
    element.crossOrigin = 'anonymous';
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error('Unable to load image for resizing.'));
    element.src = sourceUrl;
  });

  const sourceWidthPx = Math.max(1, Number(image.naturalWidth || 1));
  const sourceHeightPx = Math.max(1, Number(image.naturalHeight || 1));
  const target = getTargetPixels(draftState, sourceWidthPx, sourceHeightPx);

  const canvas = document.createElement('canvas');
  canvas.width = target.widthPx;
  canvas.height = target.heightPx;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Resize canvas is not available.');
  }

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const mimeType = draftState.sourceMimeType || inferMimeFromUrl(sourceUrl) || 'image/png';
  const dataUrl = canvas.toDataURL(mimeType);
  const bytes = decodeBase64Bytes(dataUrl);

  return {
    dataUrl,
    widthPx: target.widthPx,
    heightPx: target.heightPx,
    sourceWidthPx,
    sourceHeightPx,
    bytes,
    mimeType,
    fileType: MIME_TO_FILE_TYPE[mimeType] || 'png'
  };
};

export default function MediaEditor({ type, content, update, setContent }) {
  const [pages, setPages] = useState([]);
  const initializedSourceRef = useRef('');
  const healedDataUrlRef = useRef('');

  const patchContent = useCallback((patch) => {
    if (typeof setContent === 'function') {
      setContent({ ...content, ...patch });
      return;
    }

    Object.entries(patch).forEach(([key, value]) => update(key, value));
  }, [content, setContent, update]);

  const buildResizeDraftFromContent = useCallback((nextContent) => {
    const resizeUnit = nextContent.resizeUnit === '%' ? '%' : 'px';
    const widthLimit = resizeUnit === '%' ? 500 : 5000;
    const heightLimit = resizeUnit === '%' ? 500 : 5000;
    const resizeWidth = clamp(nextContent.resizeWidth ?? nextContent.widthPercent ?? (resizeUnit === '%' ? 100 : 1024), 1, widthLimit, resizeUnit === '%' ? 100 : 1024);
    const resizeHeight = clamp(nextContent.resizeHeight ?? nextContent.displayHeight ?? (resizeUnit === '%' ? 100 : 1024), 1, heightLimit, resizeUnit === '%' ? 100 : 1024);
    const sourceWidthPx = clamp(nextContent.resizeBaseWidth ?? nextContent.resizedWidthPx ?? 1024, 1, 12000, 1024);
    const sourceHeightPx = clamp(nextContent.resizeBaseHeight ?? nextContent.resizedHeightPx ?? 1024, 1, 12000, 1024);
    const fallbackAspect = resizeWidth / Math.max(1, resizeHeight);
    const intrinsicAspect = sourceWidthPx / Math.max(1, sourceHeightPx);
    const mimeType = String(nextContent.resizedMimeType || inferMimeFromUrl(nextContent.originalSourceUrl || nextContent.url) || 'image/png');

    return {
      resizeUnit,
      width: Math.round(resizeWidth),
      height: Math.round(resizeHeight),
      lockAspectRatio: nextContent.lockAspectRatio !== false,
      aspectRatio: clamp(nextContent.aspectRatio ?? fallbackAspect, 0.01, 100, fallbackAspect),
      intrinsicAspectRatio: clamp(nextContent.intrinsicAspectRatio ?? intrinsicAspect, 0.01, 100, intrinsicAspect),
      sourceMimeType: mimeType
    };
  }, []);

  const initialResizeDraft = buildResizeDraftFromContent(content);
  const [resizeDraft, setResizeDraft] = useState(initialResizeDraft);
  const [resizeSnapshot, setResizeSnapshot] = useState(initialResizeDraft);
  const [widthInput, setWidthInput] = useState(String(initialResizeDraft.width));
  const [heightInput, setHeightInput] = useState(String(initialResizeDraft.height));
  const [borderRadiusInput, setBorderRadiusInput] = useState(String(Math.max(0, Number(content.borderRadius ?? 10))));
  const [previewDataUrl, setPreviewDataUrl] = useState(resolveStoredImageUrl(content.url, content.originalSourceUrl));
  const [resizeMeta, setResizeMeta] = useState({
    widthPx: Number(content.resizedWidthPx || 0),
    heightPx: Number(content.resizedHeightPx || 0),
    sourceWidthPx: Number(content.resizeBaseWidth || 0),
    sourceHeightPx: Number(content.resizeBaseHeight || 0),
    bytes: Number(content.resizedBytes || 0),
    mimeType: String(content.resizedMimeType || initialResizeDraft.sourceMimeType || 'image/png'),
    fileType: MIME_TO_FILE_TYPE[String(content.resizedMimeType || initialResizeDraft.sourceMimeType || 'image/png')] || 'png'
  });
  const [resizeBusy, setResizeBusy] = useState(false);
  const [resizeError, setResizeError] = useState('');
  const safeImageUrl = resolveStoredImageUrl(content.url, content.originalSourceUrl);
  const resizeDraftSource = useMemo(() => ({
    url: content.url,
    originalSourceUrl: content.originalSourceUrl,
    resizeUnit: content.resizeUnit,
    resizeWidth: content.resizeWidth,
    resizeHeight: content.resizeHeight,
    widthPercent: content.widthPercent,
    displayHeight: content.displayHeight,
    resizeBaseWidth: content.resizeBaseWidth,
    resizeBaseHeight: content.resizeBaseHeight,
    resizedWidthPx: content.resizedWidthPx,
    resizedHeightPx: content.resizedHeightPx,
    resizedBytes: content.resizedBytes,
    resizedMimeType: content.resizedMimeType,
    lockAspectRatio: content.lockAspectRatio,
    aspectRatio: content.aspectRatio,
    intrinsicAspectRatio: content.intrinsicAspectRatio,
    borderRadius: content.borderRadius,
  }), [
    content.aspectRatio,
    content.borderRadius,
    content.displayHeight,
    content.intrinsicAspectRatio,
    content.lockAspectRatio,
    content.originalSourceUrl,
    content.resizedBytes,
    content.resizedHeightPx,
    content.resizedMimeType,
    content.resizedWidthPx,
    content.resizeBaseHeight,
    content.resizeBaseWidth,
    content.resizeHeight,
    content.resizeUnit,
    content.resizeWidth,
    content.url,
    content.widthPercent,
  ]);

  const formatBytes = (bytes) => {
    const numeric = Number(bytes);
    if (!Number.isFinite(numeric) || numeric <= 0) return '0 B';
    if (numeric < 1024) return `${numeric} B`;
    if (numeric < 1024 * 1024) return `${(numeric / 1024).toFixed(1)} KB`;
    return `${(numeric / (1024 * 1024)).toFixed(2)} MB`;
  };

  const renderPreviewForDraft = useCallback((draftState, sourceOverride) => {
    const sourceUrl = resolveStoredImageUrl(sourceOverride || resizeDraftSource.originalSourceUrl, resizeDraftSource.url);
    if (!sourceUrl) {
      setPreviewDataUrl('');
      setResizeMeta((current) => ({
        ...current,
        widthPx: 0,
        heightPx: 0,
        sourceWidthPx: 0,
        sourceHeightPx: 0,
        bytes: 0
      }));
      setResizeError('');
      return;
    }

    let cancelled = false;
    setResizeBusy(true);
    setResizeError('');

    renderResizedPreview(sourceUrl, draftState)
      .then((result) => {
        if (cancelled || !result) return;
        setPreviewDataUrl(result.dataUrl);
        setResizeMeta({
          widthPx: result.widthPx,
          heightPx: result.heightPx,
          sourceWidthPx: result.sourceWidthPx,
          sourceHeightPx: result.sourceHeightPx,
          bytes: result.bytes,
          mimeType: result.mimeType,
          fileType: result.fileType
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setResizeError(err?.message || 'Unable to render resized preview.');
      })
      .finally(() => {
        if (!cancelled) {
          setResizeBusy(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resizeDraftSource.originalSourceUrl, resizeDraftSource.url]);

  useEffect(() => {
    if (type !== 'image') return;

    const currentUrl = String(content.url || '').trim();
    const originalUrl = String(content.originalSourceUrl || '').trim();
    if (!isDataUrl(currentUrl)) return;
    if (healedDataUrlRef.current === `${currentUrl}__${originalUrl}`) return;

    healedDataUrlRef.current = `${currentUrl}__${originalUrl}`;
    if (originalUrl && !isDataUrl(originalUrl)) {
      patchContent({ url: originalUrl });
      return;
    }

    patchContent({ url: '', originalSourceUrl: '' });
    setPreviewDataUrl('');
    setResizeError('Stored inline image data was cleared. Paste the image URL again.');
  }, [content.originalSourceUrl, content.url, patchContent, type]);

  useEffect(() => {
    const nextDraft = buildResizeDraftFromContent(resizeDraftSource);
    setResizeDraft(nextDraft);
    setResizeSnapshot(nextDraft);
    setWidthInput(String(nextDraft.width));
    setHeightInput(String(nextDraft.height));
    setBorderRadiusInput(String(Math.max(0, Number(resizeDraftSource.borderRadius ?? 10))));
    setPreviewDataUrl(resolveStoredImageUrl(resizeDraftSource.url, resizeDraftSource.originalSourceUrl));
    setResizeMeta((current) => ({
      ...current,
      widthPx: Number(resizeDraftSource.resizedWidthPx || current.widthPx || 0),
      heightPx: Number(resizeDraftSource.resizedHeightPx || current.heightPx || 0),
      sourceWidthPx: Number(resizeDraftSource.resizeBaseWidth || current.sourceWidthPx || 0),
      sourceHeightPx: Number(resizeDraftSource.resizeBaseHeight || current.sourceHeightPx || 0),
      bytes: Number(resizeDraftSource.resizedBytes || current.bytes || 0),
      mimeType: String(resizeDraftSource.resizedMimeType || current.mimeType || nextDraft.sourceMimeType || 'image/png'),
      fileType: MIME_TO_FILE_TYPE[String(resizeDraftSource.resizedMimeType || current.mimeType || nextDraft.sourceMimeType || 'image/png')] || current.fileType
    }));
    setResizeError('');
  }, [buildResizeDraftFromContent, resizeDraftSource]);

  useEffect(() => {
    const sourceUrl = resolveStoredImageUrl(resizeDraftSource.originalSourceUrl, resizeDraftSource.url);
    if (!sourceUrl) {
      setPreviewDataUrl('');
      setResizeError('');
      initializedSourceRef.current = '';
      return undefined;
    }

    const currentDraft = buildResizeDraftFromContent(resizeDraftSource);
    const shouldInitFromIntrinsic = initializedSourceRef.current !== sourceUrl
      && Number(resizeDraftSource.resizeWidth ?? 1024) === 1024
      && Number(resizeDraftSource.resizeHeight ?? 1024) === 1024
      && Number(resizeDraftSource.resizedBytes || 0) === 0;

    if (!shouldInitFromIntrinsic) {
      initializedSourceRef.current = sourceUrl;
      return renderPreviewForDraft(currentDraft, sourceUrl);
    }

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (cancelled) return;

      const naturalWidth = Math.max(1, Number(image.naturalWidth || 1));
      const naturalHeight = Math.max(1, Number(image.naturalHeight || 1));
      const intrinsicAspectRatio = clamp(naturalWidth / Math.max(1, naturalHeight), 0.01, 100, 1);
      const nextDraft = currentDraft.resizeUnit === '%'
        ? {
          ...currentDraft,
          width: 100,
          height: 100,
          intrinsicAspectRatio,
          aspectRatio: intrinsicAspectRatio,
        }
        : {
          ...currentDraft,
          width: naturalWidth,
          height: naturalHeight,
          intrinsicAspectRatio,
          aspectRatio: intrinsicAspectRatio,
        };

      setResizeDraft(nextDraft);
      setResizeSnapshot(nextDraft);
      setWidthInput(String(nextDraft.width));
      setHeightInput(String(nextDraft.height));
      initializedSourceRef.current = sourceUrl;
      renderPreviewForDraft(nextDraft, sourceUrl);
    };
    image.onerror = () => {
      if (cancelled) return;
      initializedSourceRef.current = sourceUrl;
      renderPreviewForDraft(currentDraft, sourceUrl);
    };
    image.src = sourceUrl;

    return () => {
      cancelled = true;
    };
  }, [
    buildResizeDraftFromContent,
    resizeDraftSource,
    renderPreviewForDraft,
  ]);

  const toggleAspectRatioLock = () => {
    setResizeDraft((current) => {
      if (!current.lockAspectRatio) {
        return {
          ...current,
          lockAspectRatio: true,
          intrinsicAspectRatio: clamp(
            (resizeMeta.sourceWidthPx || current.width) / Math.max(1, resizeMeta.sourceHeightPx || current.height),
            0.01,
            100,
            current.intrinsicAspectRatio
          )
        };
      }
      return { ...current, lockAspectRatio: false };
    });
  };

  const commitWidthInput = () => {
    if (widthInput === '') {
      setResizeError('Width must be a number.');
      setWidthInput(String(resizeDraft.width));
      return;
    }
    const parsed = Number(widthInput);
    if (!Number.isFinite(parsed)) {
      setResizeError('Width must be a valid number.');
      setWidthInput(String(resizeDraft.width));
      return;
    }
    const before = resizeDraft;
    const nextWidth = clamp(Math.round(parsed), 1, before.resizeUnit === '%' ? 500 : 5000, before.width);
    const ratio = Math.max(0.01, before.intrinsicAspectRatio || before.aspectRatio || 1);
    const nextHeight = before.lockAspectRatio
      ? clamp(Math.round(nextWidth / ratio), 1, before.resizeUnit === '%' ? 500 : 5000, before.height)
      : before.height;
    const nextDraft = {
      ...before,
      width: nextWidth,
      height: nextHeight,
      aspectRatio: clamp(nextWidth / Math.max(1, nextHeight), 0.01, 100, before.aspectRatio)
    };
    setResizeDraft(nextDraft);
    setWidthInput(String(nextDraft.width));
    setHeightInput(String(nextDraft.height));
    setResizeError('');
    renderPreviewForDraft(nextDraft);
  };

  const commitHeightInput = () => {
    if (heightInput === '') {
      setResizeError('Height must be a number.');
      setHeightInput(String(resizeDraft.height));
      return;
    }
    const parsed = Number(heightInput);
    if (!Number.isFinite(parsed)) {
      setResizeError('Height must be a valid number.');
      setHeightInput(String(resizeDraft.height));
      return;
    }
    const before = resizeDraft;
    const nextHeight = clamp(Math.round(parsed), 1, before.resizeUnit === '%' ? 500 : 5000, before.height);
    const ratio = Math.max(0.01, before.intrinsicAspectRatio || before.aspectRatio || 1);
    const nextWidth = before.lockAspectRatio
      ? clamp(Math.round(nextHeight * ratio), 1, before.resizeUnit === '%' ? 500 : 5000, before.width)
      : before.width;
    const nextDraft = {
      ...before,
      width: nextWidth,
      height: nextHeight,
      aspectRatio: clamp(nextWidth / Math.max(1, nextHeight), 0.01, 100, before.aspectRatio)
    };
    setResizeDraft(nextDraft);
    setWidthInput(String(nextDraft.width));
    setHeightInput(String(nextDraft.height));
    setResizeError('');
    renderPreviewForDraft(nextDraft);
  };

  const changeResizeUnit = (nextUnit) => {
    if (nextUnit !== 'px' && nextUnit !== '%') return;
    if (nextUnit === resizeDraft.resizeUnit) return;

    const baseWidth = clamp(resizeMeta.sourceWidthPx || content.resizeBaseWidth || 1024, 1, 12000, 1024);
    const baseHeight = clamp(resizeMeta.sourceHeightPx || content.resizeBaseHeight || 1024, 1, 12000, 1024);
    const currentWidthPx = resizeDraft.resizeUnit === '%'
      ? clamp(Math.round((baseWidth * resizeDraft.width) / 100), 1, 5000, baseWidth)
      : resizeDraft.width;
    const currentHeightPx = resizeDraft.resizeUnit === '%'
      ? clamp(Math.round((baseHeight * resizeDraft.height) / 100), 1, 5000, baseHeight)
      : resizeDraft.height;

    const nextDraft = nextUnit === '%'
      ? {
        ...resizeDraft,
        resizeUnit: '%',
        width: clamp(Math.round((currentWidthPx / baseWidth) * 100), 1, 500, 100),
        height: clamp(Math.round((currentHeightPx / baseHeight) * 100), 1, 500, 100)
      }
      : {
        ...resizeDraft,
        resizeUnit: 'px',
        width: clamp(Math.round(currentWidthPx), 1, 5000, baseWidth),
        height: clamp(Math.round(currentHeightPx), 1, 5000, baseHeight)
      };

    setResizeDraft(nextDraft);
    setWidthInput(String(nextDraft.width));
    setHeightInput(String(nextDraft.height));
    renderPreviewForDraft(nextDraft);
  };

  const commitBorderRadiusInput = () => {
    if (borderRadiusInput === '') {
      setResizeError('Border radius must be a number.');
      setBorderRadiusInput(String(Math.max(0, Number(content.borderRadius ?? 10))));
      return;
    }

    const parsed = Number(borderRadiusInput);
    if (!Number.isFinite(parsed)) {
      setResizeError('Border radius must be a valid number.');
      setBorderRadiusInput(String(Math.max(0, Number(content.borderRadius ?? 10))));
      return;
    }

    const nextBorderRadius = clamp(parsed, 0, 80, Math.max(0, Number(content.borderRadius ?? 10)));
    update('borderRadius', nextBorderRadius);
    setBorderRadiusInput(String(nextBorderRadius));
    setResizeError('');
  };

  const saveResizeDraft = () => {
    const sourceUrl = resolveStoredImageUrl(content.originalSourceUrl, content.url);
    if (!sourceUrl) {
      setResizeError('Image URL is required before saving resize.');
      return;
    }
    patchContent({
      resizeUnit: resizeDraft.resizeUnit,
      resizeWidth: resizeDraft.width,
      resizeHeight: resizeDraft.height,
      lockAspectRatio: resizeDraft.lockAspectRatio,
      aspectRatio: resizeDraft.width / Math.max(1, resizeDraft.height),
      intrinsicAspectRatio: resizeDraft.intrinsicAspectRatio || (resizeMeta.sourceWidthPx / Math.max(1, resizeMeta.sourceHeightPx)),
      widthPercent: resizeDraft.width,
      displayHeight: resizeDraft.height,
      resizeBaseWidth: clamp(resizeMeta.sourceWidthPx || content.resizeBaseWidth || resizeMeta.widthPx || 1024, 1, 12000, 1024),
      resizeBaseHeight: clamp(resizeMeta.sourceHeightPx || content.resizeBaseHeight || resizeMeta.heightPx || 1024, 1, 12000, 1024),
      resizedWidthPx: clamp(resizeMeta.widthPx || resizeDraft.width, 1, 5000, 1024),
      resizedHeightPx: clamp(resizeMeta.heightPx || resizeDraft.height, 1, 5000, 1024),
      resizedBytes: Math.max(0, Number(resizeMeta.bytes || 0)),
      resizedMimeType: resizeMeta.mimeType || inferMimeFromUrl(sourceUrl) || 'image/png',
      originalSourceUrl: sourceUrl,
      url: sourceUrl,
    });
    setResizeSnapshot(resizeDraft);
  };

  const cancelResizeDraft = () => {
    setResizeDraft(resizeSnapshot);
    setWidthInput(String(resizeSnapshot.width));
    setHeightInput(String(resizeSnapshot.height));
    renderPreviewForDraft(resizeSnapshot);
    setResizeError('');
  };

  useEffect(() => {
    let mounted = true;

    const loadPages = async () => {
      try {
        const response = await fetch(`${API_BASE}/pagebuilder/pages`, {
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) return;

        const data = await response.json();
        if (!mounted || !Array.isArray(data)) return;
        setPages(data);
      } catch {
        // Page helper fallback: users can still enter URLs manually.
      }
    };

    loadPages();
    return () => {
      mounted = false;
    };
  }, []);

  const pageOptions = useMemo(
    () => pages.map((page) => ({ id: page.id, label: page.title || page.name || page.id })),
    [pages]
  );

  switch (type) {

    case 'image':
      return (
        <div className="pagebuilder-editor-fields">
          <Form.Group className="mb-3">
            <Form.Label>Image URL</Form.Label>
            <Form.Control
              value={safeImageUrl}
              onChange={(e) => {
                const nextUrl = e.target.value;
                if (!nextUrl.trim()) {
                  patchContent({ url: '', originalSourceUrl: '' });
                } else {
                  patchContent({ url: nextUrl, originalSourceUrl: nextUrl });
                }
              }}
              placeholder="https://…"
            />
          </Form.Group>
          {(previewDataUrl || content.url) && (
            <div className="pagebuilder-image-preview mb-3">
              <img
                src={previewDataUrl || content.url}
                alt={content.alt || 'preview'}
                className="pagebuilder-image-thumb"
                style={{
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                  marginLeft: (content.align || 'left') === 'center' ? 'auto' : (content.align || 'left') === 'right' ? 'auto' : 0,
                  marginRight: (content.align || 'left') === 'center' ? 'auto' : (content.align || 'left') === 'right' ? 0 : 'auto',
                  borderRadius: `${Math.max(0, Number(content.borderRadius ?? 10))}px`,
                }}
              />
            </div>
          )}

          <div className="pagebuilder-sublist-item mb-3">
            <div className="pagebuilder-sublist-item-header">
              <span className="pagebuilder-sublist-index">Resize</span>
            </div>

            <div className="pagebuilder-field-row mb-2">
              <Form.Group style={{ flex: 1 }}>
                <Form.Label>Border Radius (px)</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="decimal"
                  value={borderRadiusInput}
                  onChange={(e) => setBorderRadiusInput(e.target.value)}
                  onBlur={commitBorderRadiusInput}
                />
              </Form.Group>
            </div>

            <div className="pagebuilder-field-row mb-2">
              <Form.Group style={{ flex: 1 }}>
                <Form.Label>Width ({resizeDraft.resizeUnit === '%' ? '%' : 'px'})</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  value={widthInput}
                  onChange={(e) => setWidthInput(e.target.value)}
                  onBlur={commitWidthInput}
                />
              </Form.Group>

              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 6 }}>
                <button
                  type="button"
                  className="pagebuilder-block-action-btn"
                  onClick={toggleAspectRatioLock}
                  title={resizeDraft.lockAspectRatio ? 'Keep aspect ratio' : 'Aspect ratio unlocked'}
                  aria-label={resizeDraft.lockAspectRatio ? 'Keep aspect ratio' : 'Aspect ratio unlocked'}
                >
                  <i className={`bi ${resizeDraft.lockAspectRatio ? 'bi-link-45deg' : 'bi-unlink'}`} />
                </button>
              </div>

              <Form.Group style={{ flex: 1 }}>
                <Form.Label>Height ({resizeDraft.resizeUnit === '%' ? '%' : 'px'})</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  value={heightInput}
                  onChange={(e) => setHeightInput(e.target.value)}
                  onBlur={commitHeightInput}
                />
              </Form.Group>
            </div>

            <div className="pagebuilder-field-row mb-2">
              <Form.Group style={{ flex: 1 }}>
                <Form.Label>Resize Unit</Form.Label>
                <div className="d-flex align-items-center gap-3" style={{ minHeight: 38 }}>
                  <Form.Check
                    type="radio"
                    id="resize-unit-px"
                    name="resize-unit"
                    label="Pixels"
                    checked={resizeDraft.resizeUnit === 'px'}
                    onChange={() => changeResizeUnit('px')}
                  />
                  <Form.Check
                    type="radio"
                    id="resize-unit-percent"
                    name="resize-unit"
                    label="Percentage"
                    checked={resizeDraft.resizeUnit === '%'}
                    onChange={() => changeResizeUnit('%')}
                  />
                </div>
              </Form.Group>

              <Form.Group style={{ flex: 1 }}>
                <Form.Label>Resize Behavior</Form.Label>
                <Form.Text className="text-muted d-block" style={{ marginTop: 8 }}>
                  Quality and file type are preserved from the image source.
                </Form.Text>
              </Form.Group>
            </div>

            <div className="pagebuilder-field-row mb-2">
              <div className="small text-muted" style={{ flex: 1 }}>
                <div>Intrinsic: {Math.max(1, Math.round(Number(resizeMeta.sourceWidthPx || 0)))} × {Math.max(1, Math.round(Number(resizeMeta.sourceHeightPx || 0)))} px</div>
                <div>Resolution: {Math.max(1, Math.round(Number(resizeMeta.widthPx || 0)))} × {Math.max(1, Math.round(Number(resizeMeta.heightPx || 0)))} px</div>
                <div>Size: {formatBytes(resizeMeta.bytes)}</div>
                <div>Type: {(resizeMeta.fileType || 'png').toUpperCase()}</div>
              </div>
              {resizeError ? <div className="small text-danger">{resizeError}</div> : null}
            </div>

            <div className="pagebuilder-field-row">
              <button
                type="button"
                className="pagebuilder-add-btn pagebuilder-add-btn--sm"
                onClick={saveResizeDraft}
                disabled={resizeBusy || !previewDataUrl}
              >
                {resizeBusy ? 'Rendering...' : 'Save Resize'}
              </button>
              <button
                type="button"
                className="pagebuilder-block-action-btn"
                onClick={cancelResizeDraft}
                disabled={resizeBusy}
              >
                Cancel
              </button>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Alt Text</Form.Label>
            <Form.Control value={content.alt || ''} onChange={(e) => update('alt', e.target.value)} placeholder="Describe the image for accessibility…" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Caption</Form.Label>
            <Form.Control value={content.caption || ''} onChange={(e) => update('caption', e.target.value)} placeholder="Optional caption…" />
          </Form.Group>

          <div className="pagebuilder-sublist-item">
            <div className="pagebuilder-sublist-item-header">
              <span className="pagebuilder-sublist-index">Image Link</span>
            </div>

            <Form.Group className="mb-2">
              <Form.Label>Link Type</Form.Label>
              <Form.Select value={content.linkType || 'none'} onChange={(e) => update('linkType', e.target.value)}>
                <option value="none">No Link</option>
                <option value="pagebuilder">Page Builder Page</option>
                <option value="app">Application Page</option>
                <option value="external">External Website</option>
              </Form.Select>
            </Form.Group>

            {(content.linkType || 'none') === 'pagebuilder' && (
              <Form.Group className="mb-2">
                <Form.Label>Target Page Builder Page</Form.Label>
                <Form.Select value={content.pageId || ''} onChange={(e) => update('pageId', e.target.value)}>
                  <option value="">Select page...</option>
                  {pageOptions.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {(content.linkType || 'none') === 'app' && (
              <>
                <Form.Group className="mb-2">
                  <Form.Label>Application Target</Form.Label>
                  <Form.Select
                    value={content.appPath || ''}
                    onChange={(e) => update('appPath', e.target.value)}
                  >
                    <option value="">Select app page...</option>
                    {APP_PAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Or Custom App Path / Token</Form.Label>
                  <Form.Control
                    value={content.appPath || ''}
                    onChange={(e) => update('appPath', e.target.value)}
                    placeholder="/meeting/123 or {{meeting.nextMeetingLink}}"
                  />
                </Form.Group>
              </>
            )}

            {(content.linkType || 'none') === 'external' && (
              <>
                <Form.Group className="mb-2">
                  <Form.Label>External URL</Form.Label>
                  <Form.Control
                    value={content.externalUrl || ''}
                    onChange={(e) => update('externalUrl', e.target.value)}
                    placeholder="https://example.com"
                  />
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Open in new tab"
                  checked={content.openExternalInNewTab !== false}
                  onChange={(e) => update('openExternalInNewTab', e.target.checked)}
                />
              </>
            )}
          </div>
        </div>
      );

    case 'carousel': {
      const slides = content.slides || [];
      const addSlide = () => update('slides', [...slides, { id: `slide-${Date.now()}`, imageUrl: '', caption: '' }]);
      const removeSlide = (sid) => update('slides', slides.filter((s) => s.id !== sid));
      const updateSlide = (sid, field, val) => update('slides', slides.map((s) => s.id === sid ? { ...s, [field]: val } : s));
      const moveSlide = (sid, dir) => {
        const idx = slides.findIndex((s) => s.id === sid);
        const ni = idx + dir;
        if (ni < 0 || ni >= slides.length) return;
        const next = [...slides];
        [next[idx], next[ni]] = [next[ni], next[idx]];
        update('slides', next);
      };
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-field-row mb-3">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={content.title || ''}
                onChange={(e) => update('title', e.target.value)}
                placeholder="Carousel title…"
              />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Subtitle</Form.Label>
              <Form.Control
                value={content.subtitle || ''}
                onChange={(e) => update('subtitle', e.target.value)}
                placeholder="Optional subtitle…"
              />
            </Form.Group>
          </div>

          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title"><i className="bi bi-images me-1" aria-hidden="true" />Slides ({slides.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addSlide}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Slide
            </button>
          </div>
          {slides.length === 0 && <p className="pagebuilder-sublist-empty">No slides yet.</p>}
          {slides.map((slide, idx) => (
            <div key={slide.id} className="pagebuilder-sublist-item">
              <div className="pagebuilder-sublist-item-header">
                <span className="pagebuilder-sublist-index">Slide {idx + 1}</span>
                <div className="pagebuilder-block-actions">
                  <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveSlide(slide.id, -1)} disabled={idx === 0} title="Move up"><i className="bi bi-arrow-up" /></button>
                  <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveSlide(slide.id, 1)} disabled={idx === slides.length - 1} title="Move down"><i className="bi bi-arrow-down" /></button>
                  <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeSlide(slide.id)} title="Remove"><i className="bi bi-trash" /></button>
                </div>
              </div>
              <Form.Group className="mb-2">
                <Form.Label>Image URL</Form.Label>
                <Form.Control value={slide.imageUrl} onChange={(e) => updateSlide(slide.id, 'imageUrl', e.target.value)} placeholder="https://…" />
              </Form.Group>
              {slide.imageUrl && (
                <div className="pagebuilder-image-preview mb-2">
                  <img src={slide.imageUrl} alt={`Slide ${idx + 1}`} className="pagebuilder-image-thumb" />
                </div>
              )}
              <Form.Group>
                <Form.Label>Caption</Form.Label>
                <Form.Control value={slide.caption} onChange={(e) => updateSlide(slide.id, 'caption', e.target.value)} placeholder="Optional caption…" />
              </Form.Group>
            </div>
          ))}

          <div className="pagebuilder-sublist-item mt-2">
            <div className="pagebuilder-sublist-item-header">
              <span className="pagebuilder-sublist-index">Slider Behavior</span>
            </div>

            <div className="pagebuilder-field-row mb-2">
              <Form.Group style={{ flex: '0 0 170px' }}>
                <Form.Label>Transition</Form.Label>
                <Form.Select value={content.transition || 'slide'} onChange={(e) => update('transition', e.target.value)}>
                  <option value="slide">Slide</option>
                  <option value="fade">Fade</option>
                  <option value="zoom">Zoom</option>
                </Form.Select>
              </Form.Group>

              <Form.Group style={{ flex: '0 0 170px' }}>
                <Form.Label>Interval (ms)</Form.Label>
                <Form.Control
                  type="number"
                  min={800}
                  step={100}
                  value={content.intervalMs || 3500}
                  onChange={(e) => update('intervalMs', Number(e.target.value) || 3500)}
                />
              </Form.Group>
            </div>

            <div className="pagebuilder-field-row">
              <Form.Check
                type="switch"
                id="carousel-autoplay"
                label="Autoplay"
                checked={content.autoPlay !== false}
                onChange={(e) => update('autoPlay', e.target.checked)}
              />
              <Form.Check
                type="switch"
                id="carousel-arrows"
                label="Show arrows"
                checked={content.showArrows !== false}
                onChange={(e) => update('showArrows', e.target.checked)}
              />
              <Form.Check
                type="switch"
                id="carousel-indicators"
                label="Show indicators"
                checked={content.showIndicators !== false}
                onChange={(e) => update('showIndicators', e.target.checked)}
              />
              <Form.Check
                type="switch"
                id="carousel-pause"
                label="Pause on hover"
                checked={content.pauseOnHover !== false}
                onChange={(e) => update('pauseOnHover', e.target.checked)}
              />
              <Form.Check
                type="switch"
                id="carousel-loop"
                label="Loop"
                checked={content.loop !== false}
                onChange={(e) => update('loop', e.target.checked)}
              />
            </div>
          </div>
        </div>
      );
    }

    case 'collage': {
      const images = content.images || [];
      const addImage = () => update('images', [...images, { id: `img-${Date.now()}`, url: '', alt: '' }]);
      const removeImage = (iid) => update('images', images.filter((img) => img.id !== iid));
      const updateImage = (iid, field, val) => update('images', images.map((img) => img.id === iid ? { ...img, [field]: val } : img));
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title"><i className="bi bi-grid-3x3 me-1" aria-hidden="true" />Photos ({images.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addImage}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Photo
            </button>
          </div>
          {images.length === 0 && <p className="pagebuilder-sublist-empty">No photos yet.</p>}
          <div className="pagebuilder-collage-grid">
            {images.map((img, idx) => (
              <div key={img.id} className="pagebuilder-collage-item">
                <div className="pagebuilder-collage-item-header">
                  <span className="pagebuilder-sublist-index">#{idx + 1}</span>
                  <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeImage(img.id)} title="Remove"><i className="bi bi-trash" /></button>
                </div>
                {img.url && (
                  <div className="pagebuilder-image-preview mb-2">
                    <img src={img.url} alt={img.alt || `Photo ${idx + 1}`} className="pagebuilder-image-thumb" />
                  </div>
                )}
                <Form.Group className="mb-2">
                  <Form.Label>Photo URL</Form.Label>
                  <Form.Control size="sm" value={img.url} onChange={(e) => updateImage(img.id, 'url', e.target.value)} placeholder="https://…" />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Alt Text</Form.Label>
                  <Form.Control size="sm" value={img.alt} onChange={(e) => updateImage(img.id, 'alt', e.target.value)} placeholder="Description…" />
                </Form.Group>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case 'video':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>Embed URL</Form.Label>
            <Form.Control value={content.embedUrl || ''} onChange={(e) => update('embedUrl', e.target.value)} placeholder="YouTube or Vimeo URL…" />
            <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
              Supports YouTube (<code>youtube.com/watch?v=…</code> or <code>youtu.be/…</code>) and Vimeo (<code>vimeo.com/…</code>).
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control value={content.title || ''} onChange={(e) => update('title', e.target.value)} placeholder="Video title…" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Caption</Form.Label>
            <Form.Control value={content.caption || ''} onChange={(e) => update('caption', e.target.value)} placeholder="Optional caption…" />
          </Form.Group>
        </div>
      );

    case 'audio':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>Audio URL</Form.Label>
            <Form.Control value={content.url || ''} onChange={(e) => update('url', e.target.value)} placeholder="https://…/audio.mp3" />
            <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>Direct link to an MP3, OGG, or WAV file.</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control value={content.title || ''} onChange={(e) => update('title', e.target.value)} placeholder="Audio title…" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Caption</Form.Label>
            <Form.Control value={content.caption || ''} onChange={(e) => update('caption', e.target.value)} placeholder="Optional caption…" />
          </Form.Group>
        </div>
      );

    case 'pdf':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>PDF URL</Form.Label>
            <Form.Control value={content.url || ''} onChange={(e) => update('url', e.target.value)} placeholder="https://…/document.pdf" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control value={content.title || ''} onChange={(e) => update('title', e.target.value)} placeholder="Document title…" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Viewer height (px)</Form.Label>
            <Form.Control
              type="number"
              min={200}
              max={1200}
              value={content.height || 480}
              onChange={(e) => update('height', Number(e.target.value))}
            />
          </Form.Group>
        </div>
      );

    default:
      return null;
  }
}
